import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';

import { config } from 'Constants';

import { useFormik } from 'formik';

import MDButton from 'components/MDButton';
import { postKasa, updateKasa } from 'services/api/form';
import { getTags } from 'services/api/general/tag';
import Grid from '@mui/material/Grid';
import FormSelect from '../FormComponents/FormSelect';
import EmptySpace from '../FormComponents/EmptySpace';
import FormTextFieldTitle from '../FormComponents/FormTextFieldTitle';
import FormSwitch from '../FormComponents/FormSwitch';
import OutlinedFormTextField from '../FormComponents/OutlinedFormTextField';

import { validationSchemaKasa } from '../FormComponents/YupControlSchema';
import AaroLoadingScreen from 'components/Loading';

const switchStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-evenly',
};

const fetchTags = async (id) => {
    const response = await getTags(id).then((rp) => (rp.data?.result?.tags ? rp.data?.result?.tags : []));
    return response;
};
const TAG_GROUP_ID_DEMAND = config.fixedID.DEMAND_STATUS_TAG_GROUP_ID;

export const Kasa = (props) => {
    const queryClient = useQueryClient();
    const {
        data: tags,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['fetchTagGroups', TAG_GROUP_ID_DEMAND],
        queryFn: () => fetchTags(TAG_GROUP_ID_DEMAND),
        staleTime: 600000,
    });
    const user = JSON.parse(localStorage.getItem('user'));
    const [disableOnSubmit, setDisableOnSubmit] = React.useState(false);
    const { demandID, initial, id, setOpen, projectID } = props;

    const formik = useFormik({
        initialValues: {
            baslik: initial?.baslik || 'Standart Kasa',
            miktar: initial?.miktar || null,
            kalinlik: initial?.kalinlik || null,
            en: initial?.en || null,
            boy: initial?.boy || null,
            cins: initial?.cins || '',
            cinsAciklama: initial?.cinsAciklama || '',
            yapi: initial?.yapi || 'Havuz',
            lambaDerinlik: initial?.lambaDerinlik || 1.2,
            lambaGenislik: initial?.lambaGenislik || 4.5,
            koseliBombeli: initial?.koseliBombeli || 'Köşeli',
            fitil: initial?.fitil || 'L',
            pervazKanalGenisligi: initial?.pervazKanalGenisligi || 0.5,
            pervazKanalDerinligi: initial?.pervazKanalDerinligi || 3,
            girinti: initial?.girinti || 'E',
            ozelProfilTipi: initial?.ozelProfilTipi || 'Standart',
            ozelProfilAciklama: initial?.ozelProfilAciklama || '',
            dumanFitili: initial?.dumanFitili || false,
            kesim45: initial?.kesim45 || false,
            kilitKarsiligi: initial?.kilitKarsiligi || false,
            menteseKarsiligi: initial?.menteseKarsiligi || false,
            hidrolikKarsiligi: initial?.hidrolikKarsiligi || false,
            soveMi: initial?.soveMi || false,
            indirimOrani: initial?.indirimOrani || 0,
            ekNot: initial?.ekNot || '',
            yoneticiNotu: initial?.yoneticiNotu || '',
            verilenFiyat: initial?.verilenFiyat || null,
            durumu: initial?.durumu || '',
        },
        validationSchema: validationSchemaKasa,
        onSubmit: (values) => {
            setDisableOnSubmit(true);
            if (id) {
                updateKasa(id, values)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demandID] });
                        setOpen(false);
                    })
                    .catch((err) => {
                        console.log(err);
                        setDisableOnSubmit(false);
                    });
            } else {
                postKasa(demandID, values)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ['fetchDemand', demandID] });
                        setOpen(false);
                    })

                    .catch((err) => {
                        console.log(err);
                        setDisableOnSubmit(false);
                    });
            }
        },
    });
    if (isLoading) return <AaroLoadingScreen />;

    if (error) return 'Bir Hata Oluştu: ' + error.message;
    return (
        <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
                <Grid
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                    }}
                >
                    {/* Başlık */}
                    <FormTextFieldTitle
                        variant="filled"
                        fullWidth={true}
                        label="Başlık"
                        placeHolder="Örn: Kasa Başlık"
                        formik={formik}
                        itemID="baslik"
                        disabled={true}
                        selector="kasa"
                    />
                    <EmptySpace height="30px" />
                    {/* Miktar - Kalınlık - En - Boy */}
                    <Grid
                        container
                        spacing={3}
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* Miktar */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 3,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="miktar"
                                label="Miktar (Adet)"
                                placeHolder="Örn: 1000"
                                type="number"
                                formik={formik}
                                step="1"
                                adornment="AD"
                            />
                        </Grid>
                        {/* Kalınlık */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 3,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="kalinlik"
                                label="Kalınlık"
                                placeHolder="Örn: 4.1"
                                type="number"
                                formik={formik}
                                step="0.1"
                                adornment="CM"
                            />
                        </Grid>
                        {/* En */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 3,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="en"
                                label="En"
                                placeHolder="Örn: 12"
                                type="number"
                                formik={formik}
                                step="0.1"
                                adornment="CM"
                            />
                        </Grid>
                        {/* Boy */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 3,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="boy"
                                label="Boy"
                                placeHolder="Örn: 209"
                                type="number"
                                formik={formik}
                                step="0.1"
                                adornment="CM"
                            />
                        </Grid>
                    </Grid>
                    {/* Lamba Derinlik - Lamba Genişlik - Pervaz Kanal Genişlik - Pervaz Kanal Derinliği */}
                    <Grid
                        container
                        spacing={3}
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* lambaDerinlik */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 3,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="lambaDerinlik"
                                label="Lamba Derinlik (cm)"
                                type="number"
                                formik={formik}
                                step="0.1"
                                adornment="CM"
                            />
                        </Grid>
                        {/* Lamba Genişlik */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 3,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="lambaGenislik"
                                label="Lamba Genişlik (cm)"
                                type="number"
                                formik={formik}
                                step="0.1"
                                adornment="CM"
                            />
                        </Grid>
                        {/* Pervaz Kanal Genişlik */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 3,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="pervazKanalGenisligi"
                                label="Pervaz Kanal Genişliği (cm)"
                                type="number"
                                formik={formik}
                                step="0.1"
                                adornment="CM"
                            />
                        </Grid>
                        {/* Pervaz Kanal Derinliği */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 3,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="pervazKanalDerinligi"
                                label="Pervaz Kanal Derinliği (cm)"
                                type="number"
                                formik={formik}
                                step="0.1"
                                adornment="CM"
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <EmptySpace height="15px" />
                {/* Cins - Yapı - Özel Profil Tipi - Özel Profil Tipi Açıklaması*/}
                <Grid size={6}>
                    {/* Cins */}
                    <FormSelect
                        itemID="cins"
                        formik={formik}
                        inputName="Cins"
                        items={[
                            {
                                name: 'Lakelik',
                                value: 'Lakelik',
                            },
                            {
                                name: 'Desenli',
                                value: 'Desenli',
                            },
                            {
                                name: 'Kaplamalı Alpi',
                                value: 'Kaplamalı Alpi',
                            },
                            {
                                name: 'Kaplamalı Doğal',
                                value: 'Kaplamalı Doğal',
                            },
                            {
                                name: 'Kaplama Müşteriden',
                                value: 'Kaplama Müşteriden',
                            },
                            {
                                name: 'Özel Kaplama Doğal',
                                value: 'Özel Kaplama Doğal',
                            },
                            {
                                name: 'Özel Kaplama Alpi',
                                value: 'Özel Kaplama Alpi',
                            },
                        ]}
                        helperText={'Kasanın cinsi'}
                    />
                    {/* Yapı */}
                    <FormSelect
                        itemID="yapi"
                        formik={formik}
                        inputName="Yapı"
                        items={[
                            {
                                name: 'Havuz',
                                value: 'Havuz',
                            },
                            {
                                name: 'MDF',
                                value: 'MDF',
                            },
                            {
                                name: 'Yumuşak Kontrplak',
                                value: 'Yumuşak Kontrplak',
                            },
                            {
                                name: 'Sert Kontrplak',
                                value: 'Sert Kontrplak',
                            },
                            {
                                name: 'KontrTabla',
                                value: 'KontrTabla',
                            },
                            {
                                name: 'MAC',
                                value: 'MAC',
                            },
                        ]}
                        helperText={'Kasanın Yapısı'}
                    />
                    {/*Özel Profil Tipi */}
                    <FormSelect
                        itemID="ozelProfilTipi"
                        formik={formik}
                        inputName="Profil Tipi"
                        items={[
                            {
                                name: 'Standart',
                                value: 'Standart',
                            },
                            {
                                name: 'Farklı Radüsler',
                                value: 'Farklı Radüsler',
                            },
                            {
                                name: 'Özel Profil',
                                value: 'Özel Profil',
                            },
                            {
                                name: 'Özel Profil + Bıçak',
                                value: 'Özel Profil + Bıçak',
                            },
                        ]}
                        helperText={'Kasa Profil Tipi'}
                    />
                    {/* Özel Profil Tipi Açıklaması*/}
                    <OutlinedFormTextField
                        itemID="ozelProfilAciklama"
                        label="Kasa Profil Tipi Açıklaması"
                        placeHolder="Örn: Cnc deseni isteniyor"
                        formik={formik}
                        adornment=""
                    />
                </Grid>
                {/* Cins Açıklaması - Fitil - Girinti - Köşeli Bombeli*/}
                <Grid size={6}>
                    {/* Cins Açıklaması */}
                    <OutlinedFormTextField
                        itemID="cinsAciklama"
                        label="Cins Açıklaması"
                        placeHolder="Örn: A17 Teak"
                        formik={formik}
                        adornment=""
                    />
                    {/* Fitil */}
                    <FormSelect
                        itemID="fitil"
                        formik={formik}
                        inputName="Fitil"
                        items={[
                            {
                                name: 'L',
                                value: 'L',
                            },
                            {
                                name: 'T',
                                value: 'T',
                            },
                        ]}
                        helperText={'Kasanın Fitili'}
                    />
                    {/* Girinti */}
                    <FormSelect
                        itemID="girinti"
                        formik={formik}
                        inputName="Girinti"
                        items={[
                            {
                                name: 'E',
                                value: 'E',
                            },
                        ]}
                        helperText={'Kasa Girintisi'}
                    />
                    {/* Köşeli / Bombeli */}
                    <FormSelect
                        itemID="koseliBombeli"
                        formik={formik}
                        inputName="Köşeli / Bombeli"
                        items={[
                            {
                                name: 'Köşeli',
                                value: 'Köşeli',
                            },
                            {
                                name: 'Bombeli',
                                value: 'Bombeli',
                            },
                        ]}
                        helperText={'Kasa Bombesi'}
                    />
                </Grid>
                {/* Ek Not */}
                <Grid size={12}>
                    {/* Ek Not */}
                    <OutlinedFormTextField
                        itemID="ekNot"
                        label="Ek Notlarınız"
                        placeHolder="Örn: Kendi bıçaklarını verecekler"
                        formik={formik}
                    />
                </Grid>
                {/* Var Yok Seçenekleri */}
                <Grid container sx={switchStyles}>
                    {/* Duman Fitili*/}
                    <FormSwitch formik={formik} name="dumanFitili" color="secondary" label="Duman Fitili" />
                    {/* 45 Kesim*/}
                    <FormSwitch formik={formik} name="kesim45" color="secondary" label="45 Kesim" />
                    {/* Kilit Karşılığı*/}
                    <FormSwitch formik={formik} name="kilitKarsiligi" color="secondary" label="Kilit Karşılığı" />
                    {/* Menteşe Karşılığı*/}
                    <FormSwitch formik={formik} name="menteseKarsiligi" color="secondary" label="Menteşe Karşılığı" />
                    {/* Hidrolik Karşılığı*/}
                    <FormSwitch formik={formik} name="hidrolikKarsiligi" color="secondary" label="Hidrolik Karşılığı" />

                    {/* Söve Mi?*/}
                    <FormSwitch formik={formik} name="soveMi" color="secondary" label="Söve Mi" />
                </Grid>
                {/* Yönetici Alanı*/}
                <Grid
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                    }}
                >
                    <Grid container spacing={3}>
                        {/* Yönetici Notu*/}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 4,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="yoneticiNotu"
                                label="Yöneticinin Notu"
                                placeHolder="Yönetici Notu"
                                formik={formik}
                                disabled={user.role != 'superadmin'}
                            />
                        </Grid>
                        {/* Verilen Fiyat */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 4,
                            }}
                        >
                            <OutlinedFormTextField
                                itemID="verilenFiyat"
                                label="Birim Fiyat"
                                placeHolder="Birim Fiyat"
                                formik={formik}
                                step="0.01"
                                type="number"
                                // disabled={user.role != 'superadmin'}
                            />
                        </Grid>

                        {/* Teklif Durumu */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 4,
                            }}
                        >
                            {/* Girinti */}
                            <FormSelect
                                itemID="durumu"
                                formik={formik}
                                inputName="Talep Durumu"
                                items={tags.map((tag) => ({
                                    value: tag._id,
                                    name: tag.name,
                                }))}
                                helperText={'Talebin durumunu seçin'}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                    }}
                >
                    <MDButton color="dark" variant="gradient" fullWidth type="submit" disabled={disableOnSubmit}>
                        Kaydet
                    </MDButton>
                </Grid>
            </Grid>
        </form>
    );
};
